import { deepClone } from '@/_utils/deep-clone';
import {
	CoachListData,
	CoachVehicle,
	CouchGroupByVehicleEnum,
	CouchGroupByVehicles,
	SlotVehicle,
	VehicleLayout,
} from '@/new-transport/_models/transport-coach-list.model';
import { TransportSide } from '@/new-transport/_models/transport.model';

/**
 * Groups a list of coaches by their associated vehicles and categorizes them as 'before', 'after' or 'single' type.
 *
 * @param {CoachListData[]} coachList - An array of coach data entries to be grouped.
 * @param {VehicleLayout[]} layouts - An array of vehicle layouts to match with the vehicles in the coach list.
 * @param {TransportSide} side - The side of transport, either DEPARTURE or RETURN, used to determine which vehicle info to access.
 * @param {string[]} transportsToLinkNames - An array of transport names that need to be linked, affecting how coaches are grouped.
 * @returns {CouchGroupByVehicles[]} An array of grouped coach data, organized by vehicle name and type ('before' or 'after').
 *
 * Each group contains:
 * - `vehicle`: The name of the vehicle.
 * - `type`: Either 'before', 'after' or 'single'.
 * - `slots`: An array of slot details, including passenger info and seat.
 */
export function groupCouchListByVehicles(
	coachList: CoachListData[],
	layouts: VehicleLayout[],
	side: TransportSide,
	transportsToLinkNames: string[],
): CouchGroupByVehicles[] {
	if (!coachList) {
		return [];
	}

	const unassignedSlots: CouchGroupByVehicles = {
		layout: null,
		slots: [],
		type: CouchGroupByVehicleEnum.UNASSIGNED,
		vehicle: null,
		order: null,
		hasDoubledSlots: false,
	};

	const resultMap = new Map<string, CouchGroupByVehicles>();
	const hasTransportsToLink = transportsToLinkNames.length > 0;

	extractVehiclesForResultMap(hasTransportsToLink, coachList, layouts, resultMap, side);

	coachList.forEach((item) => {
		const vehicleToUse = [...item.slotVehicles].filter((v) => v.side === side);

		if (!vehicleToUse.length || (hasTransportsToLink && !hasOrderZeroAndOne(vehicleToUse))) {
			addUnassignedSlot(unassignedSlots, item);
		}

		if (hasTransportsToLink) {
			const beforeVehicle = vehicleToUse.find((v) => v.order === 0);
			const afterVehicle = vehicleToUse.find((v) => v.order === vehicleToUse.length - 1 && v.order !== 0);

			if (!!beforeVehicle) {
				pushSlotToVehicle(resultMap, beforeVehicle, item, CouchGroupByVehicleEnum.BEFORE);
			}

			if (!!afterVehicle) {
				pushSlotToVehicle(resultMap, afterVehicle, item, CouchGroupByVehicleEnum.AFTER);
			}
		} else {
			const singleVehicle = vehicleToUse.find((v) => v.order === 0);
			if (!singleVehicle) {
				return;
			}

			pushSlotToVehicle(resultMap, singleVehicle, item, CouchGroupByVehicleEnum.SINGLE);
		}
	});

	const groupedCouchList = Array.from(resultMap.values()).sort((a, b) =>
		a.vehicle.name.localeCompare(b.vehicle.name),
	);

	return unassignedSlots.slots.length ? [...groupedCouchList, unassignedSlots] : groupedCouchList;
}

/**
 * @param hasTransportsToLink - A boolean indicating whether there are transports to link.
 * @param coachList - An array of `CoachListData` objects representing the list of coaches.
 * @param layouts - An array of `VehicleLayout` objects representing the vehicle layouts.
 * @param resultMap - A `Map` where the key is a string and the value is a `CouchGroupByVehicles`,
 *                    used to store the grouped vehicles.
 * @param side - The side of the transport (`TransportSide`) to filter vehicles by.
 * This function processes the provided `coachList` to extract unique vehicles based on the specified `side`.
 * It then populates the `resultMap` with these vehicles, grouping them according to the `hasTransportsToLink` flag.
 * If `hasTransportsToLink` is true, vehicles are grouped into "BEFORE" and "AFTER" categories.
 * Otherwise, they are grouped into a "SINGLE" category.
 */
function extractVehiclesForResultMap(
	hasTransportsToLink: boolean,
	coachList: CoachListData[],
	layouts: VehicleLayout[],
	resultMap: Map<string, CouchGroupByVehicles>,
	side: TransportSide,
): void {
	const vehiclesToCreate: CoachVehicle[] = [];

	for (const item of coachList) {
		for (const slot of item.slotVehicles.filter((v) => v.side === side)) {
			const exists = vehiclesToCreate.some((v) => v.id === slot.vehicle.id);
			if (!exists) {
				vehiclesToCreate.push(slot.vehicle);
			}
		}
	}

	for (const vehicle of vehiclesToCreate) {
		if (hasTransportsToLink) {
			createResultMapVehicle(resultMap, vehicle, layouts, CouchGroupByVehicleEnum.BEFORE);
			createResultMapVehicle(resultMap, vehicle, layouts, CouchGroupByVehicleEnum.AFTER);
		} else {
			createResultMapVehicle(resultMap, vehicle, layouts, CouchGroupByVehicleEnum.SINGLE);
		}
	}
}

/**
 * Checks if the given array of SlotVehicle objects contains at least one item
 * with `order` equal to 0 and at least one item with `order` equal to 1.
 *
 * @param arr - An array of SlotVehicle objects to check.
 * @returns A boolean indicating whether the array contains both an item with `order` 0
 *          and an item with `order` 1.
 */
function hasOrderZeroAndOne(arr: SlotVehicle[]): boolean {
	const has0 = arr.some((item) => item.order === 0);
	const has1 = arr.some((item) => item.order === 1);
	return has0 && has1;
}

/**
 * Creates an entry in the result map for a given vehicle and its associated data.
 *
 * @param resultMap - A map where the key is a string combining the vehicle name and type,
 * and the value is an object containing details about the vehicle group.
 * @param vehicle - The vehicle object containing information such as name and layout.
 * @param layouts - An array of vehicle layouts to find the layout associated with the vehicle.
 * @param type - The type of the vehicle group, which determines its order.
 *
 * The function generates a key based on the vehicle name and type, and sets an entry in the
 * result map with the vehicle details, type, order, slots, layout, and a flag indicating
 * whether the slots are doubled.
 */
function createResultMapVehicle(
	resultMap: Map<string, CouchGroupByVehicles>,
	vehicle: CoachVehicle,
	layouts: VehicleLayout[],
	type: CouchGroupByVehicleEnum,
): void {
	const key = `${vehicle.name}|${type}`;
	resultMap.set(key, {
		vehicle: vehicle,
		type: type,
		order: type === CouchGroupByVehicleEnum.AFTER ? 1 : 0,
		slots: [],
		layout: deepClone(layouts.find((l) => l.id === vehicle.layout)) ?? null,
		hasDoubledSlots: false,
	});
}

/**
 * Adds an unassigned slot to the provided `unassignedSlots` object.
 *
 * This function creates a new slot based on the data from the given `coach` object
 * and appends it to the `slots` array of the `unassignedSlots` object. The new slot
 * will have its `slotVehicle`, `vehicleBefore`, and `vehicleAfter` properties set to `null`.
 *
 * @param unassignedSlots - The object containing the list of unassigned slots grouped by vehicles.
 * @param coach - The coach data used to populate the new unassigned slot.
 */
function addUnassignedSlot(unassignedSlots: CouchGroupByVehicles, coach: CoachListData): void {
	unassignedSlots.slots.push({
		doubledSlots: false,
		hasPack: coach.hasPack,
		id: coach.id,
		notes: coach.notes,
		reservation: coach.reservation,
		slotVehicle: null,
		vehicleBefore: null,
		vehicleAfter: null,
		userData: coach.userData,
		slotsInReservation: coach.slotsInReservation,
		stopDeparture: coach.stopDeparture,
		stopReturn: coach.stopReturn,
		seatnumber: coach.seatnumber,
	});
}

/**
 * Adds a slot to a vehicle in the result map, updating the slot's properties
 * and determining if there are any doubled slots for the vehicle.
 *
 * @param resultMap - A map where the key is a combination of the vehicle name and type,
 * and the value is a `CouchGroupByVehicles` object containing slot information.
 * @param singleVehicle - The vehicle to which the slot belongs.
 * @param coach - The coach data containing slot information to be added.
 * @param type - The type of grouping for the vehicle, represented by `CouchGroupByVehicleEnum`.
 *
 * @remarks
 * - If the vehicle entry does not exist in the result map, the function returns early.
 * - The function calculates whether any slots have duplicate seat IDs and updates the
 *   `doubledSlots` property for each slot accordingly.
 * - The `hasDoubledSlots` property of the vehicle entry is updated to indicate if any
 *   slots have duplicate seat IDs.
 */
function pushSlotToVehicle(
	resultMap: Map<string, CouchGroupByVehicles>,
	singleVehicle: SlotVehicle,
	coach: CoachListData,
	type: CouchGroupByVehicleEnum,
): void {
	const {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		slotVehicles,
		...rest
	} = coach;

	const entry = resultMap.get(`${singleVehicle.vehicle.name}|${type}`);

	if (!entry) {
		return;
	}

	entry.slots.push({
		...rest,
		slotVehicle: singleVehicle,
		doubledSlots: false,
		vehicleBefore: entry.type === CouchGroupByVehicleEnum.SINGLE ? null : singleVehicle?.vehicle,
		vehicleAfter: null,
	});

	const seatIdCounts = new Map<number, number>();
	for (const slot of entry.slots) {
		const seatId = slot.slotVehicle.seat.id;
		seatIdCounts.set(seatId, (seatIdCounts.get(seatId) || 0) + 1);
	}

	entry.slots = entry.slots.map((slot) => ({
		...slot,
		doubledSlots: (seatIdCounts.get(slot.slotVehicle.seat.id) || 0) > 1,
	}));

	entry.hasDoubledSlots = entry.slots.some((slot) => slot.doubledSlots);
}
